/** @jsx jsx */

import { Block } from '@chameleoncreator/creator-app';
import { jsx } from '@emotion/core';
import { setExitAction } from 'actions/setExitAction';
import { ErrorModal } from 'components/errorModal';
import Header_v2 from 'components/Header_v2/Header_v2';
import { AudioPlayer } from 'components/Layouts/AudioPlayer';
import { ThemeProvider } from 'emotion-theming';
import { createScorm } from 'factories/scorm';
import { SCORM } from 'pipwerks-scorm-api-wrapper';
import {
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBlock,
  getInteractionsFromTopicAndState,
  getTopicGroupFromState,
  getTopicsFromGroup,
} from 'selectors/block';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import getTopics from 'services/apiHelpers/getTopics';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import setCurrentInteraction from 'services/apiHelpers/setCurrentInteraction';
import getCurrentLocation from 'services/getCurrentLocation';
import getAllInteractions from 'services/apiHelpers/getAllInteractions';
import { State } from 'store/store';
import { theme } from 'theme';
import { SCORMRenderer } from '../lib/Scorm/SCORM';
import AssessmentContainer from './AssessmentContainer';
import Bookmark from './Bookmark';
import { FontManager } from './Fonts';
import Interactions_Editor from './Interactions_Editor';
import Interactions_Module from './Interactions_Module';
import { ModuleHasAudioMessage } from './ModuleHasAudioMessage';
import pubSub from 'services/pubSub';
import TopicContainer from './TopicContainer';
import windowScrollInfo from './windowScrollInfo';
import {
  reviewToolPostMessage,
  reviewToolPostMessageOnMessage,
} from 'services/reviewToolPostMessage';
import { getSCORMInitialized } from 'components/lib/Scorm/SCORM';
import { ReadsHostingContext } from './ReadsHostingContext';
import { getNavLoadingIndicator } from './navLoadingIndicator';
import { removeUndefinedEntriesFromObject } from 'services/removeUndefinedEntriesFromObject';
import { removeFalseyEntriesFromObject } from 'services/removeFalseyEntriesFromObject';
import getBlockById from 'services/apiHelpers/getBlockById';

// window.location.hash = '';
if (window.location.hash !== '') {
  // something
}

let moduleHasAudio;
let audioAcknowledged;

export const getSCORMVersion = () => {
  return SCORM.version;
};

const setCompletionStatus = scormVersion => {
  if (scormVersion === '1.2') {
    SCORM.set('cmi.core.lesson_status', 'completed');
  } else {
    SCORM.set('cmi.completion_status', 'completed');
  }
};

export const ModuleContext = createContext({
  passMark: 80,
  exit: () => {},
  setComplete: () => {},
  setCompleteAndExit: () => {},
  resetModule: () => {},
});

export const chameleonOrigins = [
  'https://review.staging.chameleoncreator.com',
  'https://staging.v2.chameleoncreator.com',
  'https://v2.chameleoncreator.com',
  'https://review.chameleoncreator.com',
  'http://localhost:8000',
  'http://localhost:3002',
  'http://localhost:3011',
];

const Module = props => {
  //
  // bookmarkStates
  // 0 no bookmarking
  // 1 pending
  // 2 resolved
  const [bookmarkState, updateBookmarkState] = useState<0 | 1 | 2>(0);

  const [editModuleHasAudioMessage, setEditModuleHasAudioMessage] = useState<
    boolean
  >(false);

  const [, setAudioAcknowledged] = useState<boolean>(window.dev() || false); // double negative ?

  let bookmarkingEnabled = useSelector(
    (state: State) => state.theme.enableBookmarking
  );

  if (bookmarkingEnabled === undefined) {
    // if the theme hasn't been manually updated 'bookmarkingEnabled' will be undefined in the theme.js output
    // in that case we interpret undefined as default/true
    bookmarkingEnabled = true;
  }

  const topicId = useSelector((state: State) => {
    return state.currentTopic;
  });

  const interactionId = useSelector((state: State) => {
    return state.currentInteraction;
  });

  const blocks = useSelector((state: State) => {
    return state.blocks;
  });

  const rootBlockId = useSelector((state: State) => {
    return state.rootBlockId;
  });

  const loadingSpinnerColor = useSelector((state: State) => {
    return state.theme.loadingSpinnerColor;
  });

  const group = useMemo(() => {
    return getTopicGroupFromState({ blocks } as State);
  }, [topicId]);

  const topicList = useMemo(() => {
    return getTopicsFromGroup({ blocks } as State, group);
  }, [topicId]);

  const dispatch = useDispatch();

  const currentTopicIndex = topicId
    ? topicList.findIndex(t => t.id === topicId)
    : 0;

  const topic: any = topicList[currentTopicIndex > -1 ? currentTopicIndex : 0];

  // update has with topic name
  // do we want to keep the spaces ' ' > %20
  // assume screen readers etc know how to manage them ( and not read that as literal )
  // const parsedTopicName = topic.props.name.replace(/#/g, '');
  // window.location.hash = parsedTopicName;

  let interactions = topic
    ? getInteractionsFromTopicAndState(
        { blocks } as State,
        getBlock(blocks, topic.id)
      )
    : [];

  const interactionNameUpdates = interactions.reduce((acc, curr) => {
    return `${acc}${curr?.props?.name}`;
  }, '');

  if (!window.dev()) {
    // only run this once. in output and when moduleHasAudio is undefined
    //
    if (moduleHasAudio === undefined) {
      moduleHasAudio = getAllInteractions().some(i => {
        const { includeAudio, audioSrc } = getBlockById(i)?.props || {};

        return includeAudio && audioSrc;
      });

      console.log('checking for module has audio', { moduleHasAudio });
    }
  }

  let scormBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'SCORM') as any
  ) as Block;

  if (!scormBlock) {
    scormBlock = createScorm();
  }

  const runExit = () => {
    if (scormBlock.props!.scormControlledExit) {
      dispatch(setExitAction());
    } else {
      dispatch(setExitAction());
      setTimeout(() => {
        window?.top?.close();
      }, 200);
    }
  };

  const moduleBlock = Object.values(blocks).find(
    ((b: Block) => b.type === 'Module') as any
  ) as Block;
  let passMark = moduleBlock.props!.passMark || 80;

  const TopicAssessmentContainer = !topic.props.asAssessment
    ? TopicContainer
    : AssessmentContainer;

  const interactionsInTopics = topicList.reduce((acc, topic) => {
    return {
      ...acc,
      [topic.id]: getInteractionsInTopic(topic),
    };
  }, {});

  let showModuleHasAudioMessage =
    moduleHasAudio && !audioAcknowledged && bookmarkState !== 1;

  if (window.dev()) {
    showModuleHasAudioMessage = editModuleHasAudioMessage;
  }

  useLayoutEffect(() => {
    windowScrollInfo(null, topicId);
  }, [topicId]);

  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    const pS = pubSub.subscribe(
      'SHOW_MODULE_HAS_AUDIO_MESSAGE',
      ({ showModuleHasAudioMessage }) => {
        setEditModuleHasAudioMessage(showModuleHasAudioMessage);
      }
    );

    return () => {
      pS();
    };
  }, []);

  useEffect(() => {
    // put this somewhere else
    const compareIds = (topicInteraction, id) => {
      return [topicInteraction.props.sId, topicInteraction.id]
        .map(sId => String(sId))
        .includes(id);
    };

    const navToHash = e => {
      e.preventDefault();

      let hash: any = String(window.location.hash)
        .replace(/#/, '')
        .split('/');

      if (hash.length !== 2) {
        return;
      }

      // for legacy we'll use the id. format will be the same as
      const [topicSid, interactionSId] = hash;

      const targetTopic = getTopics().find((t: any) => compareIds(t, topicSid));

      // this'll only be for when we're testing
      if (!targetTopic) {
        return;
      }

      const interactionsInTopic = interactionsInTopics[targetTopic.id];

      const targetInteraction = interactionsInTopic.find((i: any) =>
        compareIds(i, interactionSId)
      );

      // be safe. bail if not
      if (!targetInteraction) {
        return;
      }

      // if we're here. we're gonna nav
      // set history
      (window.locationHistory as any) = getCurrentLocation();
      //

      pubSub.publish('MODULE_NAVIGATION', {
        navToHash: 'starts',
      });

      const targetInteractionIndex = interactionsInTopic.findIndex((i: any) =>
        compareIds(i, interactionSId)
      );

      const updatedInteractionsSeen: string[] = [];
      const updatedInteractionsOpen: string[] = [];

      interactionsInTopic.forEach((i: any, index: number) => {
        if (index <= targetInteractionIndex) {
          updatedInteractionsSeen.push(i.id);
          updatedInteractionsOpen.push(i.id);
        }
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_SEEN',
        data: updatedInteractionsSeen,
      });

      dispatch({
        type: 'UPDATE_INTERACTIONS_OPEN',
        data: updatedInteractionsOpen,
      });

      targetTopic?.id && setCurrentTopic(targetTopic.id);

      const targetTopicContainerElement = document.querySelector(
        '#main'
      ) as HTMLElement;

      // const {
      //   backgroundColor,
      //   backgroundSecondaryColor,
      //   backgroundGradient,
      //   backgroundGradientAngle = 0,
      //   src,
      // } = targetTopic?.props || {};

      // we were previously 'hiding' the topic so we could fade it back in later
      // let's leave it alone and use the preloader as the transitioning effect
      if (targetTopicContainerElement) {
        // targetTopicContainerElement.classList.remove('fader_-5s');
        // targetTopicContainerElement.style.opacity = '0';
      }

      const targetSectionId = `#i${targetInteraction.id}`;
      const targetSectionElement = document.querySelector(targetSectionId);

      const backgroundColor =
        targetTopic?.props?.backgroundColor ||
        targetInteraction?.props?.background ||
        targetInteraction?.props?.backgroundColor;

      const {
        // backgroundColor,
        backgroundSecondaryColor,
        backgroundGradient,
        backgroundGradientAngle = 0,
        backgroundBlur,
        backgroundImageOpacity = 100,
        src,
      } = {
        ...targetTopic?.props,
        // ...removeFalseyEntriesFromObject(
        ...removeUndefinedEntriesFromObject(targetInteraction?.props),
        // ),
      } as any;
      //

      window.persistHistory = true;

      // remove the navLoadingIndicator if it exists. no dubUps !!!
      let navLoadingIndicator = document.querySelector(
        '#chameleon-nav-loading-indicator'
      ) as HTMLDivElement;

      if (navLoadingIndicator && document.body.contains(navLoadingIndicator)) {
        document.body.removeChild(navLoadingIndicator);
      }

      navLoadingIndicator = getNavLoadingIndicator(
        src || backgroundColor ? 'transparent' : loadingSpinnerColor
      ); // only showing the animated bubbles if. . .

      // const topicBackground = document.querySelector(
      //   `#topic-background-${targetTopic.id}`
      // );
      //
      // let topicBackgroundStyles;
      //
      // if (topicBackground) {
      //   topicBackgroundStyles = window.getComputedStyle(topicBackground);
      // }

      // UPDATE
      // in this version we account for background color etc on both topic > then interaction
      // so our 'flash' is always the background color

      // default the preloader to hidden
      // let navLoadingIndicatorVisibility = 'hidden';
      // navLoadingIndicator.style.visibility = 'hidden';

      // but if there's these values we can use. be visible to use them
      //
      const backgroundColorOrGradient = backgroundGradient
        ? `linear-gradient(${backgroundGradientAngle}deg, ${backgroundSecondaryColor}, ${backgroundColor})`
        : backgroundColor || '#ffffff';

      if (src || backgroundColorOrGradient) {
        navLoadingIndicator.style.background = src
          ? `url('${src}') center / cover no-repeat, ${backgroundColorOrGradient}`
          : backgroundColorOrGradient;

        // navLoadingIndicatorVisibility = 'visible';
        navLoadingIndicator.style.opacity = String(
          backgroundImageOpacity / 100
        );

        if (!!backgroundBlur) {
          navLoadingIndicator.style.filter = `blur(${backgroundBlur}px)`;
          navLoadingIndicator.style.transformOrigin = 'center';
          navLoadingIndicator.style.transform = 'scale(1.1)';
        }
      }

      // navLoadingIndicator.style.visibility = navLoadingIndicatorVisibility;

      document.body.appendChild(navLoadingIndicator);

      let locationTimeOut = 0;

      const tick = setInterval(() => {
        if (
          locationTimeOut > 2 &&
          document.body.contains(navLoadingIndicator)
        ) {
          console.log(
            locationTimeOut,
            document.body.contains(navLoadingIndicator),
            'we should see the navLoadingIndicator now !!!'
          );

          navLoadingIndicator.style.visibility = 'visible';
        }

        if (
          (targetSectionElement &&
            targetSectionElement.getBoundingClientRect().top < 1) ||
          locationTimeOut >= 10
        ) {
          console.log(
            `module has navigated to ${targetSectionId} in ${locationTimeOut *
              100} milliseconds ( or timed out at max 1 second ))`
          );

          if (document.body.contains(navLoadingIndicator)) {
            const t = 300; // ms
            navLoadingIndicator.style.opacity = '0';
            navLoadingIndicator.style.transition = `opacity ${t / 1000}s`;

            // we could do better to use an onTransitionEnd perhaps. . .
            setTimeout(() => {
              document.body.removeChild(navLoadingIndicator);
            }, t);
          }

          // not needed if we're not hiding the targetTopicContainerElement first. . .
          // targetTopicContainerElement.style.opacity = '1';
          // targetTopicContainerElement.style.transition = 'opacity 0.25s';

          setTimeout(() => {
            window.persistHistory = false;
          }, 750);

          clearInterval(tick);
        }
        //
        window.location.href = targetSectionId;
        locationTimeOut += 1;
      }, 100);

      pubSub.publish('MODULE_NAVIGATION', {
        navToHash: 'ends',
      });
    };

    // only add this listener when NOT in dev
    // means no weirdo nav while editing
    if (!window.dev()) {
      window.addEventListener('hashchange', navToHash);
    }

    // maybe we can toggle all this on / off when scrolling

    return () => {
      window.removeEventListener('hashchange', navToHash);
    };
  }, []);

  useEffect(() => {
    // TODO implement this check correctly

    let reviewModeEnabled;

    try {
      const href = window.top?.location?.href;

      reviewModeEnabled = href && /\/review\//g.test(href);
    } catch (err) {
      console.warn(err);
    }

    console.log({
      'window.reviewModeEnabled': window.reviewModeEnabled,
      reviewModeEnabled,
    });

    if (!(window.reviewModeEnabled || reviewModeEnabled)) {
      return;
    }

    // do not do any of this if we're connected to scorm
    const SCORMInitialized = getSCORMInitialized();
    if (SCORMInitialized) {
      return;
    }
    //

    const moduleTopicsAndInteractionsData = topicList.reduce((acc, curr) => {
      const { id } = curr;

      type CurrProps = {
        sId: string;
        name: string;
      };

      const { sId, name } = curr.props as CurrProps;

      // console.time('interactionsInTopic');
      // const interactionsInTopic = getInteractionsFromTopicAndState(
      //   { blocks } as State,
      //   getBlock(blocks, curr.id)
      // );

      const interactionsInTopic = interactionsInTopics[curr.id];

      // console.timeEnd('interactionsInTopic');

      return {
        ...acc,
        [id]: {
          id,
          sId,
          name,

          interactions: interactionsInTopic.map(interaction => {
            const { id } = interaction;
            const { sId, name } = interaction.props as CurrProps;
            return {
              id,
              sId,
              name,
            };
          }),
        },
      };
    }, {});

    reviewToolPostMessage({
      type: 'moduleTopicsAndInteractionsData',
      data: JSON.stringify(moduleTopicsAndInteractionsData),
    });

    const navToInteractionMessage = reviewToolPostMessageOnMessage(e => {
      const { origin } = e;

      if (!chameleonOrigins.includes(origin)) {
        return;
      }

      const { type, data: location } = e.data;

      if (type === 'navToInteraction') {
        console.log(type, location);

        try {
          if (window.dev()) {
            const [topicSId, interactionSId] = location.split('/');

            let topic;
            let interaction;

            const blockList = Object.values(blocks) as Block[];

            for (let i = 0; i < blockList.length; i += 1) {
              const b = blockList[i];
              const sId = b?.props?.sId;

              if (b.type === 'Topic' && sId == topicSId) {
                topic = b;
              }

              if (b.type === 'Interaction' && sId == interactionSId) {
                interaction = b;
              }

              if (topic && interaction) {
                break;
              }
            }

            if (topic && topicId !== topic.id) {
              setCurrentTopic(topic.id);
            }

            if (interaction) {
              setTimeout(() => {
                setCurrentInteraction(interaction.id);
              }, 100);
            }

            return;
          } else {
            if (location) {
              window.location.hash = location;
            }
          }
        } catch (err) {
          //
        }
      }
    });

    return () => {
      navToInteractionMessage();
    };
  }, [
    topicList.length,
    topic.props.name,
    interactions.length,
    interactionNameUpdates,
  ]);

  useLayoutEffect(() => {
    if (!rootBlockId) {
      return;
    }
    window.scrollTo(0, 0);
  }, [currentTopicIndex]);

  if (!rootBlockId) {
    return null;
  }

  return (
    <ModuleContext.Provider
      value={{
        passMark,
        exit: runExit,
        setComplete() {
          console.log('setComplete()');
          setCompletionStatus(getSCORMVersion());
        },
        setCompleteAndExit() {
          console.log('setCompleteAndExit()');
          setCompletionStatus(getSCORMVersion());

          requestAnimationFrame(() => {
            runExit();
          });
        },
        resetModule() {
          const firstInteractionId = (getAllInteractions()[0] as any) as string;

          const location = `${topicList[0]?.props?.sId}/${blocks[firstInteractionId]?.props?.sId}`;

          pubSub.publish('RESET_MODULE', {});

          dispatch({
            type: 'RESET_MODULE',
            data: {},
          });

          window.location.hash = location;
        },
      }}
    >
      <ReadsHostingContext />

      <FontManager />
      <ErrorModal>
        {error => (
          <div>
            <h1>Oh no :(</h1>
            <p>Something has gone wrong. The error message provided is:</p>
            <p>
              <strong>"{error.message}".</strong>
            </p>
            <p>
              We have been alerted to this error, however if it is consistently
              occuring please fell free to notify us by clicking on the support
              button in the bottom right hand corner.
            </p>
          </div>
        )}
      </ErrorModal>
      <SCORMRenderer key="scorm" {...scormBlock} />
      <ThemeProvider theme={theme}>
        <Header_v2 currentTopicIndex={currentTopicIndex} topics={topicList} />

        <TopicAssessmentContainer
          key={`topic${currentTopicIndex}`}
          // state={state}
          theme={theme}
          topic={topic}
        >
          {window.dev() ? (
            <Interactions_Editor
              currentTopicId={topic.id}
              currentInteractionId={interactionId}
              interactions={interactions}
            />
          ) : (
            <Interactions_Module
              key={interactions[0]?.id}
              interactions={interactions}
            />
          )}
        </TopicAssessmentContainer>

        <div
          id="menu-portal"
          style={{
            position: 'fixed',
            zIndex: 2147483647,
          }}
        />
      </ThemeProvider>

      {showModuleHasAudioMessage && (
        <ModuleHasAudioMessage
          onClose={() => {
            setAudioAcknowledged((audioAcknowledged = true));
          }}
        />
      )}

      {moduleHasAudio && (audioAcknowledged || window.dev()) && (
        <AudioPlayer key={window.dev() ? interactionId : ''} />
      )}

      {bookmarkingEnabled && (
        <Bookmark updateBookmarkState={updateBookmarkState} />
      )}
    </ModuleContext.Provider>
  );
};

export default Module;
